import { FC, useEffect, useRef, useMemo, useState } from 'react';
import Map, { Marker, MapRef, Source, Layer, GeolocateControl } from 'react-map-gl/mapbox';
import { useRepository } from "@city-sage/aurum-avis-labs-components";
import { useQuery } from "@tanstack/react-query";
import { Stop } from "@city-sage/core-client";
import { MapPin, Navigation } from 'lucide-react';
import { MAPBOX_ACCESS_TOKEN } from '../../lib/map/token';
import 'mapbox-gl/dist/mapbox-gl.css';
import { StopPopup } from '../StopPopup';
import { useDayStops } from '../../hooks/useDayStops';

interface NewStopLocation {
  latitude: number;
  longitude: number;
  title: string;
}

interface MiniDayMapViewProps {
  itineraryId: string;
  dayId: string;
  newStopLocation?: NewStopLocation;
}

const MiniDayMapView: FC<MiniDayMapViewProps> = ({ 
  itineraryId, 
  dayId, 
  newStopLocation 
}) => {
  const mapRef = useRef<MapRef | null>(null);
  const [selectedStop, setSelectedStop] = useState<Stop | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null);


  const {data: dayStops, isLoading} = useDayStops({itineraryId, dayId})


  // Create route coordinates including new stop
  const routeCoordinates = useMemo(() => {
    const coordinates: [number, number][] = [];
    
    dayStops?.forEach(stop => {
      if (stop.place.coordinates) {
        coordinates.push([stop.place.coordinates.longitude, stop.place.coordinates.latitude]);
      }
    });
    
    if (newStopLocation) {
      coordinates.push([newStopLocation.longitude, newStopLocation.latitude]);
    }
    
    return coordinates;
  }, [dayStops, newStopLocation]);

  // Create GeoJSON for the route
  const routeGeoJSON = useMemo(() => {
    if (routeCoordinates.length < 2) return null;
    
    return {
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'LineString' as const,
        coordinates: routeCoordinates
      }
    };
  }, [routeCoordinates]);

  // Calculate bounds to fit all markers
  const bounds = useMemo(() => {
    const allCoords = [...routeCoordinates];
    
    if (allCoords.length === 0) return null;
    
    const lngs = allCoords.map(coord => coord[0]);
    const lats = allCoords.map(coord => coord[1]);
    
    return {
      minLng: Math.min(...lngs),
      maxLng: Math.max(...lngs),
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats)
    };
  }, [routeCoordinates]);

  // Calculate initial view state
  const initialViewState = useMemo(() => {
    if (routeCoordinates.length === 1) {
      // Single point - center on it with good zoom
      return {
        longitude: routeCoordinates[0][0],
        latitude: routeCoordinates[0][1],
        zoom: 14
      };
    } else if (routeCoordinates.length > 1 && bounds) {
      // Multiple points - center on bounds with appropriate zoom
      const centerLng = (bounds.minLng + bounds.maxLng) / 2;
      const centerLat = (bounds.minLat + bounds.maxLat) / 2;
      
      // Calculate zoom based on bounds size
      const lngDiff = bounds.maxLng - bounds.minLng;
      const latDiff = bounds.maxLat - bounds.minLat;
      const maxDiff = Math.max(lngDiff, latDiff);
      
      let zoom = 10;
      if (maxDiff < 0.01) zoom = 16;
      else if (maxDiff < 0.1) zoom = 13;
      else if (maxDiff < 1) zoom = 10;
      else zoom = 8;
      
      return {
        longitude: centerLng,
        latitude: centerLat,
        zoom
      };
    } else {
      // No coordinates - default view (could be user's location or a reasonable default)
      return {
        longitude: -98.5795, // Center of US as default
        latitude: 39.8283,
        zoom: 4
      };
    }
  }, [routeCoordinates, bounds]);

  // Handle zooming when new stop is added or when we have multiple points
  useEffect(() => {
    if (mapRef.current && routeCoordinates.length > 0) {
      const map = mapRef.current.getMap();
      
      // Small delay to ensure map is ready
      setTimeout(() => {
        if (newStopLocation && routeCoordinates.length === 1) {
          // Only new stop - zoom to it
          map.flyTo({
            center: [newStopLocation.longitude, newStopLocation.latitude],
            zoom: 14,
            duration: 1000
          });
        } else if (bounds && routeCoordinates.length > 1) {
          // Multiple points - fit bounds
          map.fitBounds(
            [
              [bounds.minLng, bounds.minLat],
              [bounds.maxLng, bounds.maxLat]
            ],
            {
              padding: 40,
              duration: 1000,
              maxZoom: 16
            }
          );
        }
      }, 100);
    }
  }, [bounds, routeCoordinates, newStopLocation]);

  if (isLoading) {
    return (
      <div className="bg-white mt-2 p-4">
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Route Preview</p>
          <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  // Only show if we have existing stops or a new stop location
  if ((dayStops?.length ?? 0) === 0 && !newStopLocation) {
    return null;
  }

  return (
    <div className="bg-white mt-2 p-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-700">Route Preview</p>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {(dayStops?.length ?? 0) > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Existing ({(dayStops?.length ?? 0)})</span>
              </div>
            )}
            {newStopLocation && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>New stop</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-200 relative">
          <Map
            ref={mapRef}
            mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
            style={{ width: '100%', height: '100%' }}
            mapStyle="mapbox://styles/mapbox/streets-v12"
            initialViewState={initialViewState}
          >
            {/* Route line */}
            {routeGeoJSON && (
              <Source id="route" type="geojson" data={routeGeoJSON}>
                <Layer
                  id="route-line"
                  type="line"
                  paint={{
                    'line-color': '#3b82f6',
                    'line-width': 3,
                    'line-opacity': 0.8
                  }}
                />
              </Source>
            )}

            {/* Existing stops */}
            {dayStops?.map((stop, index) => (
              <Marker
                key={stop.id}
                longitude={stop.place.coordinates!.longitude}
                latitude={stop.place.coordinates!.latitude}
                anchor="center"
              >
                <div 
                  className="relative cursor-pointer hover:scale-110 transition-transform"
                  onClick={() => {
                    setSelectedStop(stop);
                    const map = mapRef.current;
                    const mapContainer = map?.getContainer();
                    if (map && mapContainer && stop.place.coordinates) {
                      // Get the exact screen position of the pin
                      const pinScreenCoords = map.project([
                        stop.place.coordinates.longitude,
                        stop.place.coordinates.latitude
                      ]);
                      
                      const rect = mapContainer.getBoundingClientRect();
                      let x = pinScreenCoords.x;
                      let y = pinScreenCoords.y;
                      
                      // Simple bounds checking
                      const popupWidth = 300;
                      const popupHeight = 120;
                      
                      // Keep popup within horizontal bounds
                      if (x + popupWidth / 2 > rect.width) {
                        x = rect.width - popupWidth / 2 - 10;
                      }
                      if (x - popupWidth / 2 < 0) {
                        x = popupWidth / 2 + 10;
                      }
                      
                      // Keep popup above the pin, but show below if no space
                      if (y - popupHeight - 15 < 0) {
                        y = y + 30; // Show below the pin
                      }
                      
                      setPopupPosition({ x, y });
                    }
                  }}
                >
                  <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium shadow-lg border-2 border-white">
                    {stop.index}
                  </div>
                </div>
              </Marker>
            ))}

            {/* New stop */}
            {newStopLocation && (
              <Marker
                longitude={newStopLocation.longitude}
                latitude={newStopLocation.latitude}
                anchor="center"
              >
                <div className="relative">
                  <div className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg border-2 border-white">
                    <MapPin className="h-3 w-3" />
                  </div>
                  {/* Pulsing animation for new stop */}
                  <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-30"></div>
                </div>
              </Marker>
            )}

            {/* Geolocation Control */}
            <GeolocateControl
              position="top-right"
              positionOptions={{ enableHighAccuracy: true }}
              trackUserLocation={true}
              showAccuracyCircle={false}
            />
          </Map>
          
          {/* Stop Popup */}
          {selectedStop && popupPosition && (
            <div 
              className="absolute pointer-events-none"
              style={{
                left: `${popupPosition.x}px`,
                top: `${popupPosition.y}px`,
                transform: 'translate(-50%, -100%)',
                marginTop: '-10px',
                zIndex: 1000
              }}
            >
              <div className="pointer-events-auto">
                <StopPopup
                  stop={selectedStop}
                  onClose={() => {
                    setSelectedStop(null);
                    setPopupPosition(null);
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Stop count summary */}
        {((dayStops?.length ?? 0) > 0 || newStopLocation) && (
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <Navigation className="h-3 w-3" />
              <span>
                {newStopLocation 
                  ? `${(dayStops?.length ?? 0) + 1} stops planned`
                  : `${(dayStops?.length ?? 0)} stops`
                }
              </span>
            </div>
            {newStopLocation && (
              <span className="text-green-600 font-medium">
                + {newStopLocation.title}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniDayMapView; 